import { Tooltip, TooltipContent, TooltipTrigger } from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';

import { ToolBadge } from '@/shared/ui/ToolBadge';

import type { PageHeaderToolGroupProps } from './PageHeader.types';

/**
 * Tool badge plus the title or breadcrumb that names the current record.
 *
 * The negative margin is what makes the badge read as sitting on the header's
 * 24px inset: the badge is a filled tile, so aligning its box to the inset
 * pushes its glyph visibly inboard of every text-led header.
 *
 * On the tool's own home the badge is decorative, because a control offering to
 * go where you already are is a dead end: the caller omits `onBadgeClick` and it
 * renders as a static tile with no tooltip and nothing for a screen reader,
 * which the page title beside it already names.
 *
 * Where it does navigate the badge sits in a plain button, which carries the
 * name and the focus ring the decorative badge cannot.
 */
export function PageHeaderToolGroup(props: PageHeaderToolGroupProps) {
  const { t } = useTranslation('common');
  const badge = (testId?: string) => (
    <ToolBadge
      toolId={props.toolId}
      category={props.category}
      icon={props.icon}
      size="md"
      data-testid={testId}
    />
  );
  const label = props.onBadgeClick
    ? t('components.pageHeader.backToToolHome', { tool: props.tool })
    : undefined;

  return (
    <div className="-ml-3.5 flex min-w-0 items-center gap-sm">
      {props.onBadgeClick ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={label}
              onClick={props.onBadgeClick}
              data-testid="page-header-tool-badge"
              className="flex shrink-0 cursor-pointer rounded-sm outline-none focus-visible:focus-ring"
            >
              {badge()}
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom">{label}</TooltipContent>
        </Tooltip>
      ) : (
        badge('page-header-tool-badge')
      )}
      {props.children}
    </div>
  );
}
